import React from 'react';
import { StyleSheet, View } from 'react-native';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useBillingPalette } from '@/components/Settings/billing/BillingGroup';
import { CheckRow, money } from './AutoTopupEditor';
import {
  AUTO_TOPUP_BONUS_PERCENT,
  bonusCreditsFor,
  grantedCreditsFor,
  suggestedMonthlyLimitForPackPrice,
  thresholdForOneClickOptIn,
} from '@/lib/autoTopupOffer';

export default function AutoTopupCheckoutOptIn({ pack, checked, onChange, disabled = false }) {
  const palette = useBillingPalette();
  if (!pack) return null;
  const threshold = thresholdForOneClickOptIn(pack.credits);
  if (!threshold) return null;
  const granted = grantedCreditsFor(pack.credits);
  const bonus = bonusCreditsFor(pack.credits);
  const border = palette.isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)';

  return (
    <View style={[styles.card, { borderColor: checked ? palette.ink : border, borderWidth: checked ? 1.5 : StyleSheet.hairlineWidth }]}>
      <CheckRow label="Enable auto top-up after this purchase" checked={checked} onChange={onChange} disabled={disabled}>
        <View style={styles.header}>
          <View style={styles.copy}>
            <View style={styles.titleRow}>
              <Text size="s" weight="semibold" color="primary">Turn on auto top-up</Text>
              <View style={[styles.bonus, { backgroundColor: palette.tileFill }]}>
                <Text size="xxs" weight="bold" color="primary">{`+${AUTO_TOPUP_BONUS_PERCENT}% credits`}</Text>
              </View>
            </View>
            <Text size="xs" color="secondary">
              {`Get ${granted.toLocaleString()} credits instead of ${pack.credits.toLocaleString()} every time it refills, so you never stop mid-task.`}
            </Text>
          </View>
          <View style={[styles.tile, { backgroundColor: palette.ink }]}>
            <Icon name="arrows-rotate" size={12} color={palette.inkInverse} />
          </View>
        </View>
      </CheckRow>
      {checked && (
        <View style={[styles.terms, { borderTopColor: border }]}>
          <Text size="xs" color="primary">
            I authorize {money(pack.price)} for {granted.toLocaleString()} credits, including {bonus.toLocaleString()} bonus credits, when my balance falls below {threshold.toLocaleString()}, up to {money(suggestedMonthlyLimitForPackPrice(pack.price))} per month.
          </Text>
          <Text size="xs" color="secondary">
            Uses your saved payment method. New cards wait 24 hours before the first automatic charge. One-time discounts do not apply. Manage or turn it off anytime in Billing.
          </Text>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: 14,
    paddingHorizontal: 14,
    paddingVertical: 12,
    gap: 10,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
  },
  copy: {
    flex: 1,
    gap: 3,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  bonus: {
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderRadius: 999,
  },
  tile: {
    width: 26,
    height: 26,
    borderRadius: 7,
    alignItems: 'center',
    justifyContent: 'center',
  },
  terms: {
    borderTopWidth: StyleSheet.hairlineWidth,
    paddingTop: 10,
    gap: 6,
  },
});
